import React from 'react';
import formatDistance from 'date-fns/formatDistance';
import parseISO from 'date-fns/parseISO';
import { enUS, ru } from 'date-fns/locale';

import { formatDate } from 'helpers/formatDate';
import { getTranslation } from 'helpers/getTranslation';
import { modifyPreviewForTableRender } from 'helpers/modifyPreviewForTableRender';

import { Table } from 'components/shared/Table';
import { Button } from 'components/shared/Button';
import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import { StatusTag } from 'components/StatusTag';
import { EmptyState } from 'components/EmptyState';
import { CoursePreview } from 'components/CoursePreview';
import { PublishStatusTag } from 'components/PublishStatusTag';

import { getCoursesTableColumns } from './CoursesSection.tableConfig';

import {
  getCourseType,
  getCourseStatusText,
  getCourseStatusVariant,
  isCourseFullyCompleted,
  showCertificateIssueAction,
} from './CoursesSection.helpers';

import styles from './CoursesSection.module.scss';

const cellRenderer = (
  {
    item,
    key,
    minWidth,
    maxWidth,
  }: { item: any; key: any; minWidth: any; maxWidth: any },
  DefaultCell: any,
  locale: any,
  {
    onIssueCertificateForCourseClick,
    issuingCertificateCourseId,
  }: {
    onIssueCertificateForCourseClick: (course: any) => void;
    issuingCertificateCourseId: string | null;
  },
) => {
  switch (key) {
    case 'title': {
      return (
        <div
          className={styles.titleCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <CoursePreview className={styles.preview} preview={item.preview} />
          <Typography
            className={styles.title}
            variant="body3"
            weight="medium"
            mode="compact"
          >
            {item.title || getTranslation('common.text.untitled')}
          </Typography>
        </div>
      );
    }
    case 'type': {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {getCourseType(item[key])}
        </DefaultCell>
      );
    }
    case 'updatedAt': {
      return (
        <DefaultCell key={key} style={{ minWidth, maxWidth }}>
          {item.updatedAt
            ? formatDistance(parseISO(item.updatedAt), new Date(), {
                addSuffix: true,
                locale: locale === 'ru' ? ru : enUS,
              })
            : '-'}
        </DefaultCell>
      );
    }
    case 'courseProgress': {
      return (
        <div
          className={styles.progressCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <StatusTag
            variant={getCourseStatusVariant(item.progress, item.startedAt)}
          >
            {getCourseStatusText({
              progress: item.progress,
              startedAt: item.startedAt,
              completedLessons: item.completedLessons.length,
              lessonsCount: item.lessonsCount,
            })}
          </StatusTag>
        </div>
      );
    }
    case 'courseStatus': {
      return (
        <div
          className={styles.statusCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <PublishStatusTag variant={item.status.toLowerCase()} />
        </div>
      );
    }
    case 'lessonsProgress': {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {item.completedLessons.length}/{item.lessonsCount}
        </DefaultCell>
      );
    }
    case 'startedAt': {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {item[key] ? formatDate(item[key], 'dd MMM, yyyy', locale) : '-'}
        </DefaultCell>
      );
    }
    case 'completedAt': {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {item[key] ? formatDate(item[key], 'dd MMM, yyyy', locale) : '-'}
        </DefaultCell>
      );
    }
    case 'certificateAction': {
      if (!showCertificateIssueAction(item)) {
        return (
          <div
            className={styles.certificateCell}
            key={key}
            style={{ minWidth, maxWidth }}
          />
        );
      }

      const courseId = item.id ?? item._id;
      const isCompleted = isCourseFullyCompleted(item);
      const isIssuing = issuingCertificateCourseId === courseId;

      const tooltipText = isCompleted
        ? getTranslation(
            'student_page.courses_section.issue_certificate_tooltip',
          )
        : getTranslation(
            'student_page.courses_section.issue_certificate_disabled_tooltip',
          );

      return (
        <div
          className={styles.certificateCell}
          key={key}
          style={{ minWidth, maxWidth }}
        >
          <Tooltip
            text={tooltipText}
            triggerElement={
              <Button
                mode="icon"
                variant="ghost"
                size="sm"
                iconName="filePdf"
                disabled={!isCompleted || isIssuing}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onIssueCertificateForCourseClick(item);
                }}
              />
            }
          />
        </div>
      );
    }
    default: {
      return (
        <DefaultCell key={key} minWidth={minWidth} maxWidth={maxWidth}>
          {item[key] || '-'}
        </DefaultCell>
      );
    }
  }
};

const CoursesSection = ({
  locale,
  isRootAdmin,
  courses,
  onCourseClick,
  onManageCoursesAccessClick,
  onGrantProAccessClick,
  onIssueCertificateForCourseClick,
  issuingCertificateCourseId,
}: {
  locale: any;
  isRootAdmin: any;
  courses: any;
  onCourseClick: any;
  onManageCoursesAccessClick: any;
  onGrantProAccessClick: any;
  onIssueCertificateForCourseClick: any;
  issuingCertificateCourseId: string | null;
}) => (
  <div className={styles.section}>
    {isRootAdmin && (
      <div className={styles.header}>
        <Typography variant="subtitle">
          {getTranslation('student_page.courses_section.title')}
        </Typography>
        <Button
          className={styles.action}
          size="md"
          variant="link"
          onClick={onGrantProAccessClick}
        >
          {getTranslation('student_page.courses_section.pro_access_button')}
        </Button>
        <Button size="md" variant="link" onClick={onManageCoursesAccessClick}>
          {getTranslation('common.button.manage_access')}
        </Button>
      </div>
    )}
    {courses.length > 0 ? (
      <Table
        columns={getCoursesTableColumns({
          withCertificateAction: isRootAdmin,
        })}
        actions={[]}
        data={modifyPreviewForTableRender(courses)}
        cellRenderer={(props: any, DefaultCell: any) =>
          cellRenderer(props, DefaultCell, locale, {
            onIssueCertificateForCourseClick,
            issuingCertificateCourseId,
          })
        }
        onRowClick={onCourseClick}
      />
    ) : (
      <EmptyState
        className={styles.emptyState}
        icon="courses"
        title={getTranslation('student_page.courses_section.empty_state.title')}
        text={getTranslation('student_page.courses_section.empty_state.text')}
      />
    )}
  </div>
);

export default React.memo(CoursesSection) as any;
